import * as React from 'react';
import clsx from 'clsx';
import { CheckCircleIcon } from '@heroicons/react/24/solid';
import { AddInput } from '@/components/common/AddInput';
import { ConfiguredBadge } from './ConfiguredBadge';

interface ConfigTagsProps {
  label: string;
  description: string;
  options: string[];
  selected: string[];
  onChange: (selected: string[]) => void;
  icon?: React.ReactNode;
  allowCustom?: boolean;
  // Allow defining grid columns (default to 1)
  gridCols?: 1 | 2;
  // Badge + undo support
  isModified?: boolean;
  onUndo?: () => void;
}

export const ConfigTags: React.FC<ConfigTagsProps> = ({
  label,
  description,
  options,
  selected,
  onChange,
  icon,
  allowCustom = false,
  gridCols = 1,
  isModified,
  onUndo,
}) => {
  const [customTag, setCustomTag] = React.useState('');

  const toggleTag = (tag: string) => {
    if (selected.includes(tag)) {
      onChange(selected.filter((item) => item !== tag));
    } else {
      onChange([...selected, tag]);
    }
  };

  const handleAddCustom = () => {
    const val = customTag.trim();
    if (val && !selected.includes(val)) {
      onChange([...selected, val]);
      setCustomTag('');
    }
  };

  return (
    <div
      className={clsx(
        'relative flex w-full flex-col gap-6',
        'rounded-20 border border-brand-300 bg-gray-25 p-6'
      )}
    >
      {/* Configured Badge */}
      {isModified && onUndo && <ConfiguredBadge onUndo={onUndo} />}

      <div className='flex items-start gap-4 pr-16'>
        {icon && (
          <div className='flex size-[40px] shrink-0 items-center justify-center rounded-[13px] bg-brand-750 text-white'>
            {icon}
          </div>
        )}
        <div className='flex flex-col gap-1'>
          <h4 className='font-sans text-20 font-medium leading-27 text-dark-100'>{label}</h4>
          <p className='font-sans text-13 font-normal leading-[18px] text-gray-600'>
            {description}
          </p>
        </div>
      </div>

      <div
        className={clsx(
          'grid gap-3 md:pl-[64px]',
          // Dynamically switch between 1 column or 2 columns based on prop
          gridCols === 2 ? 'grid-cols-1 sm:grid-cols-2' : 'grid-cols-1'
        )}
      >
        {/* 1. Custom Input (Moved to Top) */}
        {allowCustom && (
          <div
            className={clsx(
              // Ensure the input spans full width in 2-column layout
              gridCols === 2 && 'col-span-1 sm:col-span-2'
            )}
          >
            <AddInput
              value={customTag}
              onChange={(e) => setCustomTag(e.target.value)}
              onAdd={handleAddCustom}
              onKeyDown={(e) => e.key === 'Enter' && handleAddCustom()}
              placeholder='Add custom hobby or interest...'
            />
          </div>
        )}

        {/* 2. Standard Options */}
        {options.map((option) => {
          const isSelected = selected.includes(option);
          return (
            <button
              key={option}
              onClick={() => toggleTag(option)}
              type='button'
              className={clsx(
                'flex w-full items-center justify-between rounded-10 px-4 py-3 transition-all',
                'border text-left font-sans text-14 font-medium',
                isSelected
                  ? 'border-gray-750 bg-brand-750 text-white'
                  : 'border-gray-310 bg-white text-brand-800 hover:bg-gray-50'
              )}
            >
              <span>{option}</span>
              {isSelected ? (
                <CheckCircleIcon className='size-5 text-brand-300' />
              ) : (
                <div className='size-5 rounded-full border border-gray-300' />
              )}
            </button>
          );
        })}

        {/* 3. Custom Tags Display - Purple cards in same grid layout */}
        {allowCustom &&
          selected
            .filter((sel) => !options.includes(sel))
            .map((tag) => (
              <div
                key={tag}
                className={clsx(
                  'flex w-full items-center justify-between rounded-10 px-4 py-3',
                  // Custom Tag Style: purple background card, no border
                  'bg-purple-250',
                  'cursor-default'
                )}
              >
                {/* Text in brand-800 */}
                <span className='font-sans text-16 font-normal leading-27 text-brand-800'>
                  {tag}
                </span>

                {/* Click to remove */}
                <button
                  type='button'
                  onClick={() => toggleTag(tag)}
                  className='transition-opacity hover:opacity-80'
                  aria-label={`Remove ${tag}`}
                >
                  <CheckCircleIcon className='size-[20px] text-brand-750' />
                </button>
              </div>
            ))}
      </div>
    </div>
  );
};
